import { Component, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { COUNTRY_TIMEZONE_DATA, CountryTimezone } from './country-timezone-data';
import { ActivatedRoute, Router } from '@angular/router';
import { Utils } from 'src/app/utils/utils';
import { CreateAccountHelper } from './create-account-helper';
import { AuthserviceService } from 'src/app/services/authservice.service';
import { RouteConstants } from 'src/app/constants/route-constants';
import { NgSelectComponent } from '@ng-select/ng-select';
import { AppSessionStorageService } from 'src/app/services/utils/session-storage.service';
 
@Component({
  selector: 'app-create-account',
  templateUrl: './create-account.component.html',
  styleUrls: ['./create-account.component.css'],
})
export class CreateAccountComponent extends CreateAccountHelper implements OnInit {
  @ViewChild(NgSelectComponent) timezoneSelect!: NgSelectComponent;
  isSubmittingData = false;
  faEye = faEye;
  faEyeSlash = faEyeSlash;
  errorMessage = '';
  showInstallerReleatedInput = false
  override countries: CountryTimezone[] = COUNTRY_TIMEZONE_DATA;
  override filteredTimezones: CountryTimezone[] = [];
 
  constructor(private readonly router: Router, private readonly route: ActivatedRoute, private readonly authService: AuthserviceService) {
    super();
  }
 
/**
* Lifecycle hook that initializes the component.
* - Extracts and sets query parameters.
* - Sets up form defaults for country and timezone.
* - Handles installer-specific logic.
*/
ngOnInit(): void {
  this.initializeFromQueryParams();
  this.setupDefaultCountryAndTimezone();
}
 
/**
* Extracts query parameters and populates inputModel.
* Also sets session storage and username in the form.
*/
private initializeFromQueryParams(): void {
  this.route.queryParams?.subscribe((value: any) => {
    this.inputModel.token = value['token'] ?? '';
    this.inputModel.user_type = value['user_type'] ?? '';
    this.inputModel.username = Utils.getUsernameFromToken(this.inputModel.token ?? '');
    this.inputModel.slug = value['slug'] ?? '';
    AppSessionStorageService.registerUserType = this.inputModel.user_type;;
    this.createAccountForm.get('username')?.setValue(this.inputModel.username);
 
    if (this.inputModel.user_type === 'installer') {
      this.showInstallerReleatedInput = true;
    }
  });
}
/**
* Handles the account creation form submission.
*
* This method:
* - Validates the form,
* - Maps form values to the input model,
* - Sends a request to create an account,
* - Redirects the user based on the response:
*    - To success page if account creation is successful,
*    - To 'User Exists' page if the user already exists,
*    - To 'Restricted Access' page for other 403 errors.
* If the form is invalid, it marks all fields as touched to display validation errors.
*/
async onSubmitButtonTapped(): Promise<void> {
  // Clear any previous error message and show loading state
  this.errorMessage = '';
  this.isSubmittingData = true;
  // Proceed only if the form is valid
  if (this.createAccountForm.valid) {
    // Map form values to input model used for account creation
    this.inputModel = this.mapFormToModel(this.createAccountForm);
    // Call the auth service to create a new account with the input model
    const response = await this.authService.createAccount(this.inputModel);
    // Handle successful account creation
    if (response.success) {
      this.isSubmittingData = false;
      // Navigate to account created success screen
      this.router.navigate([RouteConstants.ACCOUNT_CREATED], { replaceUrl: true });
    // Handle case when the user already exists
    } else if (response.responseCode == 403 && response.message == 'This user already exists') {
      this.isSubmittingData = false;
      // Redirect to user already exists screen
      this.router.navigate([RouteConstants.USER_EXIST], { replaceUrl: true });
    // Handle other 403 Forbidden cases
    } else if (response.responseCode == 403) {
      this.isSubmittingData = false;
      // Redirect to restricted access screen
      this.router.navigate([RouteConstants.RESTRICTED_ACCESS], { replaceUrl: true });
    }
  } else {
    // Mark all form fields as touched to show validation errors
    this.createAccountForm.markAllAsTouched();
    // Reset submitting state
    this.isSubmittingData = false;
  }
}
 
/**
* Custom validator to check if the password and confirm password fields match.
*
* This validator:
* - Retrieves the values of 'password' and 'confirmPassword' controls from the form group.
* - Returns null if both values match (i.e., the validation passes).
* - Returns a validation error object `{ passwordsMismatch: true }` if they do not match.
*
* @param group - The AbstractControl representing the form group containing password fields.
* @returns A ValidationErrors object if the passwords do not match, otherwise null.
*/
isValidPassword(group: AbstractControl): ValidationErrors | null {
  // Get the values from the form controls
  const password = group.get('password')?.value;
  const confirmPassword = group.get('confirmPassword')?.value;
  // Check if passwords match; return validation error if they don't
  return password === confirmPassword ? null : { passwordsMismatch: true };
}
 
/**
* Clears the selected timezone value from the form.
*
* This method:
* - Sets the 'timezone' form control value to null,
* - Closes the timezone dropdown/select UI component.
*/
onClearTimezone() {
  // Reset the 'timezone' field in the form to null
  this.createAccountForm.get('timezone')?.setValue(null);
  // Close the timezone select dropdown (assumes it has a close() method)
  this.timezoneSelect.close();
}
 
 
}
 
 